import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { customInstance } from '@/shared/api/mutator/custom-instance';

const BASE = '/frontend/admin/ess-branches';

export interface BranchSummary {
    branchCode: string;
    branchName: string;
    districtCode: string | null;
    emkopoSyncStatus: 'SYNCED' | 'PENDING' | 'FAILED';
    emkopoLastSyncDate: string | null;
    emkopoLastSyncError: string | null;
    isActive: boolean;
}

interface PageResponse<T> {
    content: T[];
    totalElements: number;
    totalPages: number;
    size: number;
    number: number;
}

interface ListParams {
    page?: number;
    size?: number;
    status?: string;
}

interface UpdateDistrictParams {
    branchCode: string;
    districtCode: string;
}

const QUERY_KEY = 'ess-branches';

export const useBranches = (params: ListParams = {}) => {
    const { page = 0, size = 20, status } = params;
    return useQuery({
        queryKey: [QUERY_KEY, page, size, status],
        queryFn: () =>
            customInstance<PageResponse<BranchSummary>>({
                url: BASE,
                method: 'GET',
                params: { page, size, ...(status ? { status } : {}) },
            }),
    });
};

export const useSyncBranch = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () =>
            customInstance<{ success: boolean; message: string }>({
                url: `${BASE}/sync`,
                method: 'POST',
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
        },
    });
};

export const useUpdateDistrict = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ branchCode, districtCode }: UpdateDistrictParams) =>
            customInstance<{ success: boolean; message: string }>({
                url: `${BASE}/${branchCode}/district`,
                method: 'PUT',
                data: { districtCode },
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
        },
    });
};
